import { useEffect, useState } from 'react';
import { api, ApiError } from './api';

interface Result<T> {
  key: string | null;
  data: T | null;
  error: string | null;
}

/** Small fetch hook for read-only screens; refetches when `path` changes or reload() is called. */
export function useApi<T>(path: string | null): {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
} {
  const [tick, setTick] = useState(0);
  const [result, setResult] = useState<Result<T>>({ key: null, data: null, error: null });
  const key = path === null ? null : `${path}#${tick}`;

  useEffect(() => {
    if (path === null || key === null) return;
    let cancelled = false;
    api<T>(path)
      .then((data) => {
        if (!cancelled) setResult({ key, data, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof ApiError ? err.message : 'Could not load. Please try again.';
        setResult({ key, data: null, error: message });
      });
    return () => {
      cancelled = true;
    };
  }, [path, key]);

  const current = result.key === key;
  return {
    data: current ? result.data : null,
    error: current ? result.error : null,
    loading: key !== null && !current,
    reload: () => setTick((t) => t + 1),
  };
}
